/**
 * Global Network Error Handler
 * 
 * Prevents random page refreshes caused by network 404 responses
 * during connectivity issues by providing comprehensive error recovery.
 */

interface NetworkErrorDetails {
  error: any;
  url?: string;
  operation?: string;
  timestamp: number;
}

class NetworkErrorHandler {
  private errorQueue: NetworkErrorDetails[] = [];
  private recoveryAttempts = new Map<string, number>();
  private readonly MAX_RECOVERY_ATTEMPTS = 3;
  private readonly ERROR_QUEUE_LIMIT = 10;
  
  constructor() {
    this.initializeGlobalErrorHandling();
    this.initializeNetworkMonitoring();
  }

  /**
   * Initialize global error handling to catch uncaught network errors
   */
  private initializeGlobalErrorHandling(): void {
    // Catch unhandled promise rejections (common for fetch errors)
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      if (this.isNetworkConnectivity404(error)) {
        console.log('🌐 Global handler caught network 404 - preventing page reload');
        event.preventDefault(); // Prevent default browser error handling
        this.handleNetworkConnectivity404(error, 'unhandled-promise');
      }
    });

    // Catch general JavaScript errors that might be network-related
    window.addEventListener('error', (event: ErrorEvent) => {
      const error = event.error;
      
      if (this.isNetworkRelated(error) || this.isNetworkConnectivity404(error)) {
        console.log('🌐 Global handler caught network error - attempting graceful recovery');
        this.handleNetworkError(error, event.filename || 'unknown');
      }
    });
  }

  /**
   * Initialize network status monitoring
   */
  private initializeNetworkMonitoring(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('🌐 NetworkErrorHandler: Connection restored, processing queued errors');
        this.processErrorQueue();
      });

      window.addEventListener('offline', () => {
        console.log('🌐 NetworkErrorHandler: Connection lost, queuing errors for retry');
      });
    }
  }

  /**
   * Check if error is a network connectivity 404 (vs legitimate resource 404)
   */
  private isNetworkConnectivity404(error: any): boolean {
    return error?.status === 404 && (
      error?.message?.includes('fetch') ||
      error?.message?.includes('ERR_NETWORK') ||
      error?.message?.includes('ERR_CONNECTION') ||
      error?.message?.includes('Failed to fetch') ||
      !navigator.onLine ||
      error?.code === 'NETWORK_ERROR' ||
      // Directus-specific network 404 patterns
      (error?.message?.includes('404') && (
        error?.message?.includes('directus') ||
        error?.url?.includes('directus') ||
        !error?.response?.data
      ))
    );
  }

  /**
   * Check if error is network-related
   */
  private isNetworkRelated(error: any): boolean {
    const networkPatterns = [
      'fetch',
      'NetworkError',
      'Failed to fetch',
      'ERR_NETWORK',
      'ERR_CONNECTION',
      'ERR_INTERNET_DISCONNECTED',
      'NETWORK_FAILURE',
      'Connection refused',
      'timeout'
    ];

    const errorMessage = error?.message || error?.toString() || '';
    return networkPatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Handle network connectivity 404 errors gracefully
   */
  private handleNetworkConnectivity404(error: any, operation: string): void {
    const errorKey = `${operation}-404`;
    const attempts = this.recoveryAttempts.get(errorKey) || 0;

    if (attempts < this.MAX_RECOVERY_ATTEMPTS) {
      console.log(`🔄 Network 404 recovery attempt ${attempts + 1}/${this.MAX_RECOVERY_ATTEMPTS}`);
      
      // Queue error for retry when connection is restored
      this.queueError({
        error,
        operation,
        timestamp: Date.now()
      });

      this.recoveryAttempts.set(errorKey, attempts + 1);
      
      // Show user-friendly notification instead of page reload
      this.showConnectivityNotification();
      
    } else {
      console.warn('⚠️ Max recovery attempts reached for 404 errors - showing persistent notification');
      this.showPersistentConnectivityNotification();
    }
  }

  /**
   * Handle general network errors
   */
  private handleNetworkError(error: any, source: string): void {
    console.log('🌐 Handling network error from:', source, error);
    
    // Queue for retry when connection improves
    this.queueError({
      error,
      operation: source,
      timestamp: Date.now()
    });

    // Show gentle user notification
    this.showConnectivityNotification();
  }

  /**
   * Queue errors for retry when connection is restored
   */
  private queueError(errorDetails: NetworkErrorDetails): void {
    if (this.errorQueue.length >= this.ERROR_QUEUE_LIMIT) {
      this.errorQueue.shift(); // Remove oldest error
    }
    
    this.errorQueue.push(errorDetails);
    console.log(`📝 Queued network error (${this.errorQueue.length} total)`);
  }

  /**
   * Process queued errors when connection is restored
   */
  private processErrorQueue(): void {
    if (this.errorQueue.length === 0) return;

    console.log(`🔄 Processing ${this.errorQueue.length} queued network errors`);
    
    // Clear recovery attempts since connection is restored
    this.recoveryAttempts.clear();
    
    // Clear the queue
    this.errorQueue = [];
    
    // Hide any persistent notifications
    this.hideConnectivityNotifications();
    
    // Optional: Trigger app state refresh instead of page reload
    this.triggerSoftAppRefresh();
  }

  /**
   * Show user-friendly connectivity notification
   */
  private showConnectivityNotification(): void {
    // Check if notification already exists
    if (document.getElementById('network-connectivity-notification')) {
      return;
    }

    const notification = document.createElement('div');
    notification.id = 'network-connectivity-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 300px;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease;
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="font-size: 18px;">🌐</div>
        <div>
          <div style="font-weight: 500;">Connection Issue</div>
          <div style="font-size: 12px; opacity: 0.9;">Retrying automatically...</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    requestAnimationFrame(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideConnectivityNotifications();
    }, 5000);
  }

  /**
   * Show persistent connectivity notification for repeated failures
   */
  private showPersistentConnectivityNotification(): void {
    const notification = document.getElementById('network-connectivity-notification');
    if (notification) {
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="font-size: 18px;">⚠️</div>
          <div>
            <div style="font-weight: 500;">Connection Problems</div>
            <div style="font-size: 12px; opacity: 0.9;">Check your internet connection</div>
          </div>
        </div>
      `;
      
      // Make it more prominent
      notification.style.background = 'linear-gradient(135deg, #dc2626, #b91c1c)';
    }
  }

  /**
   * Hide all connectivity notifications
   */
  private hideConnectivityNotifications(): void {
    const notification = document.getElementById('network-connectivity-notification');
    if (notification) {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }
  }

  /**
   * Trigger soft app refresh instead of page reload
   */
  private triggerSoftAppRefresh(): void {
    // Dispatch custom event that app can listen to for soft refresh
    window.dispatchEvent(new CustomEvent('network-recovery', {
      detail: { timestamp: Date.now() }
    }));
    
    console.log('📡 Network recovery complete - soft refresh triggered');
  }

  /**
   * Manual method to handle known 404s gracefully
   */
  public handleKnown404Error(error: any, operation: string): boolean {
    if (this.isNetworkConnectivity404(error)) {
      this.handleNetworkConnectivity404(error, operation);
      return true; // Handled
    }
    return false; // Not handled - let normal error flow continue
  }

  /**
   * Check current connectivity status
   */
  public isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Get current error queue status
   */
  public getQueueStatus(): { count: number; oldestError?: number } {
    return {
      count: this.errorQueue.length,
      oldestError: this.errorQueue[0]?.timestamp
    };
  }
}

// Export singleton instance
export const networkErrorHandler = new NetworkErrorHandler();

// Export class for testing
export { NetworkErrorHandler };